"use client";

import { SearchLg } from "@untitledui/icons";
import type {
	FC,
	FocusEventHandler,
	PointerEventHandler,
	ReactNode,
	RefAttributes,
	RefObject,
} from "react";
import {
	isValidElement,
	useCallback,
	useContext,
	useRef,
	useState,
} from "react";
import type { Placement } from "react-aria";
import type {
	ComboBoxProps as AriaComboBoxProps,
	GroupProps as AriaGroupProps,
	ListBoxProps as AriaListBoxProps,
} from "react-aria-components";
import {
	ComboBox as AriaComboBox,
	Group as AriaGroup,
	Input as AriaInput,
	ListBox as AriaListBox,
	ComboBoxStateContext,
} from "react-aria-components";
import { HintText } from "@/components/base/input/hint-text";
import { Label } from "@/components/base/input/label";
import { Popover } from "@/components/base/select/popover";
import { useResizeObserver } from "@/hooks/use-resize-observer";
import { cx } from "@/utils/cx";
import { isReactComponent } from "@/utils/is-react-component";
import {
	type CommonProps,
	SelectContext,
	type SelectItemType,
	sizes,
} from "./select-shared";

interface ComboBoxProps
	extends Omit<AriaComboBoxProps<SelectItemType>, "children" | "items">,
		RefAttributes<HTMLDivElement>,
		CommonProps {
	placeholder?: string;
	shortcut?: boolean;
	items?: SelectItemType[];
	popoverClassName?: string;
	shortcutClassName?: string;
	placement?: Placement;
	size?: "sm" | "md" | "lg";
	hideRequiredIndicator?: boolean;
	/** Leading icon component displayed before the input. */
	icon?: FC | ReactNode;
	children: AriaListBoxProps<SelectItemType>["children"];
}

interface ComboBoxValueProps extends AriaGroupProps {
	size: "sm" | "md" | "lg";
	shortcut: boolean;
	placeholder?: string;
	shortcutClassName?: string;
	icon?: FC | ReactNode;
	onFocus?: FocusEventHandler;
	onPointerEnter?: PointerEventHandler;
	ref?: RefObject<HTMLDivElement | null>;
	isInvalid?: boolean;
}

const ComboBoxValue = ({
	size,
	shortcut,
	placeholder,
	shortcutClassName,
	icon: IconProp,
	isInvalid,
	...otherProps
}: ComboBoxValueProps) => {
	const state = useContext(ComboBoxStateContext);

	const value = state?.selectedItem?.value || null;
	const inputValue = state?.inputValue || null;

	const first = inputValue?.split(value?.supportingText)?.[0] || "";
	const last = inputValue?.split(first)[1];

	return (
		<AriaGroup
			{...otherProps}
			aria-invalid={isInvalid}
			className={({ isFocusWithin, isDisabled }) =>
				cx(
					"relative flex w-full items-center gap-2 rounded-lg bg-primary shadow-xs outline-hidden ring-1 ring-primary ring-inset transition-shadow duration-100 ease-linear",
					!!isDisabled && "cursor-not-allowed opacity-50",
					!!isFocusWithin && "ring-2 ring-brand",

					// Icon styles
					"*:data-icon:shrink-0 *:data-icon:text-fg-quaternary",
					"py-0!",
					"aria-invalid:border-error_subtle aria-invalid:ring-error_subtle dark:aria-invalid:ring-error_subtle",

					sizes[size].root
				)
			}
		>
			{isReactComponent(IconProp) ? (
				<IconProp
					aria-hidden="true"
					className="pointer-events-none"
					data-icon
				/>
			) : isValidElement(IconProp) ? (
				IconProp
			) : (
				<SearchLg
					aria-hidden="true"
					className="pointer-events-none"
					data-icon
				/>
			)}

			<div className="relative flex w-full items-center">
				{!!inputValue && (
					<span
						aria-hidden="true"
						className={cx(
							"-translate-y-1/2 absolute top-1/2 z-0 inline-flex w-full truncate",
							sizes[size].textContainer
						)}
					>
						<p className={cx("font-medium text-primary", sizes[size].text)}>
							{first}
						</p>
						{!!last && (
							<p className={cx("-ml-0.75 text-tertiary", sizes[size].text)}>
								{last}
							</p>
						)}
					</span>
				)}

				<AriaInput
					className={cx(
						"z-10 w-full appearance-none bg-transparent py-2! pr-3! text-transparent caret-alpha-black/90 outline-none placeholder:text-placeholder focus:outline-hidden disabled:cursor-not-allowed",
						sizes[size].text
					)}
					placeholder={placeholder}
				/>
			</div>

			{!!shortcut && (
				<div
					className={cx(
						"absolute inset-y-0.5 right-0.5 z-10 hidden items-center rounded-r-[inherit] bg-linear-to-r from-transparent to-40% to-bg-primary pl-8 md:flex",
						sizes[size].shortcut,
						shortcutClassName
					)}
				>
					<span
						aria-hidden="true"
						className="pointer-events-none select-none rounded px-1 py-px font-medium text-quaternary text-xs ring-1 ring-secondary ring-inset"
					>
						⌘K
					</span>
				</div>
			)}
		</AriaGroup>
	);
};

export const ComboBox = ({
	placeholder = "Search",
	shortcut = true,
	size = "md",
	children,
	items,
	shortcutClassName,
	icon,
	hideRequiredIndicator,
	placement = "bottom",
	...otherProps
}: ComboBoxProps) => {
	const placeholderRef = useRef<HTMLDivElement>(null);
	const [popoverWidth, setPopoverWidth] = useState("");

	// Resize observer for popover width
	const onResize = useCallback(() => {
		if (!placeholderRef.current) {
			return;
		}

		const divRect = placeholderRef.current?.getBoundingClientRect();

		setPopoverWidth(`${divRect.width}px`);
	}, [placeholderRef, setPopoverWidth]);

	useResizeObserver({
		ref: placeholderRef,
		box: "border-box",
		onResize,
	});

	return (
		<SelectContext.Provider value={{ size }}>
			<AriaComboBox menuTrigger="focus" {...otherProps} aria-label="Combobox">
				{(state) => (
					<div className="flex flex-col gap-1.5">
						{!!otherProps?.label && (
							<Label
								isRequired={hideRequiredIndicator ? false : state.isRequired}
								tooltip={otherProps.tooltip}
							>
								{otherProps.label}
							</Label>
						)}

						<ComboBoxValue
							icon={icon}
							isInvalid={state.isInvalid}
							onFocus={onResize}
							onPointerEnter={onResize}
							placeholder={placeholder}
							ref={placeholderRef}
							// This is a workaround to correctly calculating the trigger width
							// while using ResizeObserver wasn't 100% reliable.
							shortcut={shortcut}
							shortcutClassName={shortcutClassName}
							size={size}
						/>

						<Popover
							className={otherProps.popoverClassName}
							placement={placement}
							size={size}
							style={{ width: popoverWidth }}
							triggerRef={placeholderRef}
						>
							{!!items && items.length > 0 ? (
								<AriaListBox className="size-full outline-hidden" items={items}>
									{children}
								</AriaListBox>
							) : (
								<div className="px-4 py-2">
									<Label>Nenhum resultado encontrado</Label>
								</div>
							)}
						</Popover>

						{!!otherProps?.hint && (
							<HintText
								className={cx(size === "sm" && "text-xs")}
								isInvalid={state.isInvalid}
							>
								{otherProps.hint}
							</HintText>
						)}
					</div>
				)}
			</AriaComboBox>
		</SelectContext.Provider>
	);
};
